import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  TableTotal,
  formatCurrency,
  formatNumber,
} from "@/components/ui/standardized-table";
import { TrendingUp, TrendingDown, ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import {
  getMetaAdsAdPerformanceApiV1IntegrationsMetaAdsAdPerformanceGet,
  proxyAdsDashboardKpiApiV1AdsDashboardPlatformKpisMetricGet,
  proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  HourlyTableItem,
  DemographicsTableItem,
  PositioningTableItem,
  UrlDestinationTableItem,
  CreativeTypeTableItem,
  CampaignTableItem,
  MetaAdsAdPerformanceItem,
} from "@/backend-sdk/client/types.gen";
import { useToast } from "@/hooks/use-toast";
import { formatDecimal, formatSignedPercent } from "@/utils/numberFormat";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { useAccount } from "@/contexts/account.context";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";

// ─── Filter mode types ────────────────────────────────────────────────────────

type AdFilterMode = "contains" | "equals" | "startsWith" | "regex" | "in";

const AD_FILTER_OPTIONS: { value: AdFilterMode; label: string }[] = [
  { value: "equals", label: "Igual a" },
  { value: "contains", label: "Contém" },
  { value: "startsWith", label: "Começa com" },
  { value: "regex", label: "Regex" },
  { value: "in", label: "Em" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatRoas = (v: number) =>
  Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const renderDelta = (value: unknown) => {
  const num = Number(value);
  if (!Number.isFinite(num))
    return <span className="text-muted-foreground">—</span>;
  const pos = num > 0;
  const neg = num < 0;
  const Icon = pos ? TrendingUp : neg ? TrendingDown : null;
  const color = pos
    ? "text-green-500"
    : neg
      ? "text-red-500"
      : "text-muted-foreground";
  return (
    <div className="flex items-center justify-end">
      {Icon && <Icon className={`w-3 h-3 mr-1 ${color}`} />}
      <span className={color}>{formatSignedPercent(num)}</span>
    </div>
  );
};

/** Bar cell: numeric value + horizontal bar proportional to max */
const renderBarCell = (
  value: unknown,
  allValues: number[],
  color: string,
  formatter: (v: number) => string,
) => {
  const n = Number(value) || 0;
  if (n === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const max = Math.max(...allValues, 1);
  const pct = Math.min((n / max) * 100, 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <span className="text-xs whitespace-nowrap min-w-[50px] text-right">
        {formatter(n)}
      </span>
      <div className="flex-1 h-4 bg-muted/30 rounded overflow-hidden">
        <div
          className="h-full rounded"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

/** Heatmap cell (colored background) */
const renderHeatmap = (
  value: unknown,
  allValues: number[],
  rgb: string,
  formatter: (v: number) => string,
) => {
  const n = Number(value) || 0;
  if (n === 0 || allValues.length === 0)
    return <span className="text-xs text-muted-foreground">—</span>;
  const min = Math.min(...allValues.filter((v) => v > 0));
  const max = Math.max(...allValues);
  const range = max - min;
  const ratio = range > 0 ? (n - min) / range : 0.5;
  const opacity = 0.2 + ratio * 0.6;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs"
      style={{ backgroundColor: `rgba(${rgb}, ${opacity})` }}
    >
      {formatter(n)}
    </span>
  );
};

// ─── KPI types ────────────────────────────────────────────────────────────────

type KpiState = {
  value: number;
  change: number;
  trend: "up" | "down" | "neutral";
  unit?: string;
};

type AdsDashboardPaginationState = {
  current_page: number;
  total_items: number;
  total_pages: number;
  page_size: number;
};

const getAdsDashboardTrend = (trend: string | undefined): KpiState["trend"] => {
  return trend === "up" || trend === "down" || trend === "neutral"
    ? trend
    : "neutral";
};

const KPI_METRICS_LIST = [
  { slug: "receita", label: "Receita", fmt: formatCurrency },
  { slug: "investimento", label: "Investimento", fmt: formatCurrency },
  { slug: "roas", label: "ROAS", fmt: formatRoas },
  { slug: "compras", label: "Compras", fmt: (v: number) => formatNumber(v) },
  { slug: "ticket-medio", label: "Ticket Médio", fmt: formatCurrency },
  {
    slug: "taxa-conversao",
    label: "Taxa Conversão",
    fmt: (v: number) => `${formatDecimal(v, 2)}%`,
  },
  { slug: "cpa", label: "CPA", fmt: formatCurrency },
  { slug: "cps", label: "CPS (GA)", fmt: formatCurrency },
] as const;

/**
 * O que cada KPI precisa. Todos saem da conta do Meta Ads; o CPS divide o investimento pelas
 * sessões do GA4, e sem o GA4 o servidor devolve zero.
 */
const FONTES_DO_KPI = {
  meta: ["meta_ads"],
  metaComGa4: ["meta_ads", "ga4"],
} as const satisfies Record<string, readonly RequisitoDeFonte[]>;

const fontesDoKpi = (slug: string): readonly RequisitoDeFonte[] =>
  slug === "cps" ? FONTES_DO_KPI.metaComGa4 : FONTES_DO_KPI.meta;

// ─── Shared column builder ──────────────────────────────────────────────────

const buildBreakdownColumns = (
  dimensionKey: string,
  dimensionHeader: string,
  invValues: number[],
  compValues: number[],
  recValues: number[],
  roasValues: number[],
): TableColumn[] => [
  { key: dimensionKey, header: dimensionHeader, align: "left" as const },
  {
    key: "pagDestino",
    header: "Pág. Destino",
    align: "right" as const,
    render: (v) => {
      const n = Number(v || 0);
      return n === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        formatNumber(n)
      );
    },
  },
  {
    key: "custoPagDestino",
    header: "Custo Pág. Destino",
    align: "right" as const,
    render: (v) => {
      const n = Number(v || 0);
      return n === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        formatCurrency(n)
      );
    },
  },
  {
    key: "txConversao",
    header: "TX Conversão",
    align: "right" as const,
    render: (v) => {
      const n = Number(v || 0);
      return n === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        `${formatDecimal(n, 2)}%`
      );
    },
  },
  {
    key: "investimento",
    header: "Investimento",
    align: "right" as const,
    sortable: true,
    render: (v) => renderHeatmap(v, invValues, "20, 184, 166", formatCurrency),
  },
  {
    key: "compras",
    header: "Compras",
    align: "right" as const,
    sortable: true,
    render: (v) =>
      renderHeatmap(v, compValues, "168, 85, 247", (n) => formatNumber(n)),
  },
  {
    key: "receita",
    header: "Receita",
    align: "right" as const,
    sortable: true,
    render: (v) => renderHeatmap(v, recValues, "6, 182, 212", formatCurrency),
  },
  {
    key: "roas",
    header: "ROAS",
    align: "right" as const,
    sortable: true,
    render: (v) => renderHeatmap(v, roasValues, "34, 197, 94", formatRoas),
  },
  { key: "delta", header: "% Δ", align: "right" as const, render: renderDelta },
];

/**
 * O "Total Geral" que a rota publica, no formato que `StandardizedTable` desenha.
 *
 * <M-125> As tabelas URL DESTINO e AD CREATIVE OBJECT TYPE respondiam sem linha nenhuma até
 * o item 6 da auditoria da Magnata. Dar-lhes linhas sem lhes dar rodapé teria acrescentado à
 * tela os padrões **A** e **B** da própria auditoria de uma vez: `StandardizedTable`
 * auto-calcula o "Total Geral" quando não recebe um, e o que ele sabe fazer é somar coluna
 * por coluna — somar ROAS não dá ROAS, e o que ele tem em mãos são as **vinte linhas da
 * página**, não o conjunto. É o mesmo defeito que M-219 corrigiu na ANÁLISE MÍDIA PAGA e que
 * M-220 e M-222 corrigiram nas tabelas de campanha, de conjunto e de anúncio.
 *
 * `undefined` devolve a tabela ao rodapé automático — o que acontece quando a rota não
 * publica rodapé, e o que as outras quatro tabelas desta tela ainda fazem (M-223).
 */
const totalDoServidor = (
  linha: UrlDestinationTableItem | CreativeTypeTableItem | undefined,
): TableTotal | undefined => (linha ? { ...linha } : undefined);

// ─── Pagination helper ────────────────────────────────────────────────────
const Paginator = ({
  current,
  total,
  totalItems,
  pageSize,
  onPage,
}: {
  current: number;
  total: number;
  totalItems: number;
  pageSize: number;
  onPage: (p: number) => void;
}) =>
  total > 1 ? (
    <div className="flex items-center justify-end gap-4 mt-2">
      <span className="text-xs text-muted-foreground">
        {(current - 1) * pageSize + 1} -{" "}
        {Math.min(current * pageSize, totalItems)} / {totalItems}
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => onPage(Math.max(1, current - 1))}
          disabled={current <= 1}
          className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-muted"
        >
          ‹
        </button>
        <button
          onClick={() => onPage(Math.min(total, current + 1))}
          disabled={current >= total}
          className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-muted"
        >
          ›
        </button>
      </div>
    </div>
  ) : null;

// ─── Component ────────────────────────────────────────────────────────────────

const MelhoresResultadosMetaPage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { selectedAccount: accountCtx } = useAccount();
  const { toast } = useToast();
  const navigate = useNavigate();

  // ─── State ────────────────────────────────────────────────────────────────
  const [kpis, setKpis] = useState<Record<string, KpiState>>({});
  // Começa ligado: com `false`, a primeira pintura saía antes do efeito e desenhava "—".
  const [kpisLoading, setKpisLoading] = useState(true);
  const [erroKpis, setErroKpis] = useState(false);

  // Hourly
  const [hourlyData, setHourlyData] = useState<HourlyTableItem[]>([]);
  // As tabelas começam ligadas pelo mesmo motivo dos KPIs: com `false`, a primeira
  // pintura saía antes do efeito e dizia "Nenhum dado encontrado".
  const [hourlyLoading, setHourlyLoading] = useState(true);

  // Demographics
  const [demographics, setDemographics] = useState<DemographicsTableItem[]>([]);
  const [demographicsLoading, setDemographicsLoading] = useState(true);
  const [demographicsPag, setDemographicsPag] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    page_size: 20,
  });
  const [demographicsPage, setDemographicsPage] = useState(1);

  // Positioning
  const [positioning, setPositioning] = useState<PositioningTableItem[]>([]);
  const [positioningLoading, setPositioningLoading] = useState(true);
  const [positioningPag, setPositioningPag] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    page_size: 20,
  });
  const [positioningPage, setPositioningPage] = useState(1);

  // URL Destination
  const [urlDestination, setUrlDestination] = useState<
    UrlDestinationTableItem[]
  >([]);
  const [urlDestinationLoading, setUrlDestinationLoading] = useState(true);
  const [urlDestinationPag, setUrlDestinationPag] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    page_size: 20,
  });
  const [urlDestinationPage, setUrlDestinationPage] = useState(1);

  // Creative Type
  const [urlDestinationTotal, setUrlDestinationTotal] = useState<
    UrlDestinationTableItem | undefined
  >(undefined);
  const [creativeType, setCreativeType] = useState<CreativeTypeTableItem[]>([]);
  const [creativeTypeTotal, setCreativeTypeTotal] = useState<
    CreativeTypeTableItem | undefined
  >(undefined);
  const [creativeTypeLoading, setCreativeTypeLoading] = useState(true);
  const [creativeTypePag, setCreativeTypePag] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    page_size: 20,
  });
  const [creativeTypePage, setCreativeTypePage] = useState(1);

  // Campaigns
  const [campaigns, setCampaigns] = useState<CampaignTableItem[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsPag, setCampaignsPag] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    page_size: 20,
  });
  const [campaignsPage, setCampaignsPage] = useState(1);

  // Ads
  const [ads, setAds] = useState<MetaAdsAdPerformanceItem[]>([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [adsPag, setAdsPag] = useState({
    current_page: 1,
    total_results: 0,
    total_pages: 0,
  });
  const [adsPage, setAdsPage] = useState(1);
  const [adSearch, setAdSearch] = useState("");
  const [adFilterMode, setAdFilterMode] = useState<AdFilterMode>("contains");
  const [adFilterOpen, setAdFilterOpen] = useState(false);
  const adFilterRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!adFilterOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        adFilterRef.current &&
        !adFilterRef.current.contains(e.target as Node)
      ) {
        setAdFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [adFilterOpen]);

  // ─── Derived filter params ────────────────────────────────────────────────
  const params = useMemo(() => {
    if (!filterState) return null;
    const customerUuid =
      accountCtx?.customer_uuid ||
      localStorage.getItem("active_customer_uuid") ||
      "";
    return {
      customer_uuid: customerUuid,
      start_date: formatDateToISO(filterState.dateRange.startDate),
      end_date: formatDateToISO(filterState.dateRange.endDate),
      comparison:
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period",
    };
  }, [filterState, accountCtx]);

  // ─── Generic table fetcher ────────────────────────────────────────────────
  const fetchTable = useCallback(
    async (
      slug: string,
      page: number,
      pageSize: number,
      setLoading: (l: boolean) => void,
    ) => {
      if (!params?.customer_uuid) {
        setLoading(false);
        return { items: [], pagination: null, totalRow: undefined };
      }
      setLoading(true);
      try {
        const { data } =
          await proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet({
            path: { platform: "meta", table: slug },
            query: {
              customer_uuid: params.customer_uuid,
              start_date: params.start_date,
              end_date: params.end_date,
              comparison: params.comparison,
              page,
              page_size: pageSize,
            },
            throwOnError: true,
          });
        return {
          items: (data as unknown as { items?: unknown[] }).items ?? [],
          pagination: (
            data as unknown as { pagination?: AdsDashboardPaginationState }
          ).pagination,
          // <M-125> O "Total Geral" das duas tabelas do criativo. Ver `totalDoServidor`.
          totalRow: (data as unknown as { total_row?: unknown }).total_row,
        };
      } catch (e) {
        console.error(`Erro ${slug}:`, e);
        return { items: [], pagination: null, totalRow: undefined };
      } finally {
        setLoading(false);
      }
    },
    [params],
  );

  // ─── Fetch KPIs ───────────────────────────────────────────────────────────
  const fetchKpis = useCallback(async () => {
    if (!params?.customer_uuid) {
      setKpisLoading(false);
      return;
    }
    setKpisLoading(true);
    try {
      const results = await Promise.all(
        KPI_METRICS_LIST.map(async ({ slug }) => {
          const { data } =
            await proxyAdsDashboardKpiApiV1AdsDashboardPlatformKpisMetricGet({
              path: { platform: "meta", metric: slug },
              query: {
                customer_uuid: params.customer_uuid,
                start_date: params.start_date,
                end_date: params.end_date,
                comparison: params.comparison,
              },
              throwOnError: true,
            });
          return [
            slug,
            {
              value: data.value,
              change: data.change_percentage ?? 0,
              trend: getAdsDashboardTrend(data.trend),
              unit: data.unit,
            },
          ] as const;
        }),
      );
      setKpis(Object.fromEntries(results));
      setErroKpis(false);
    } catch (e) {
      console.error("Erro KPIs:", e);
      setErroKpis(true);
      toast({ title: "Erro ao carregar KPIs", variant: "destructive" });
    } finally {
      setKpisLoading(false);
    }
  }, [params, toast]);

  // ─── Fetch Ads ────────────────────────────────────────────────────────────
  const fetchAds = useCallback(async () => {
    if (!params?.customer_uuid) {
      setAdsLoading(false);
      return;
    }
    setAdsLoading(true);
    try {
      const { data: resp } =
        await getMetaAdsAdPerformanceApiV1IntegrationsMetaAdsAdPerformanceGet({
          query: {
            start_date: params.start_date,
            end_date: params.end_date,
            page: adsPage,
            page_size: 20,
            sort_by: "spend",
            sort_order: "desc",
          },
          throwOnError: true,
        });
      const d = resp;
      setAds(d.results ?? []);
      setAdsPag({
        current_page: d.current_page ?? 1,
        total_results: d.total_results ?? 0,
        total_pages: d.total_pages ?? 0,
      });
    } catch (e) {
      console.error("Erro ads:", e);
    } finally {
      setAdsLoading(false);
    }
  }, [params, adsPage]);

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchKpis();
  }, [fetchKpis]);
  useEffect(() => {
    fetchTable("hourly", 1, 24, setHourlyLoading).then((r) => {
      setHourlyData(r.items as HourlyTableItem[]);
    });
  }, [fetchTable]);
  useEffect(() => {
    fetchTable(
      "demographics",
      demographicsPage,
      20,
      setDemographicsLoading,
    ).then((r) => {
      setDemographics(r.items as DemographicsTableItem[]);
      if (r.pagination)
        setDemographicsPag({
          current_page: r.pagination.current_page ?? 1,
          total_items: r.pagination.total_items ?? 0,
          total_pages: r.pagination.total_pages ?? 0,
          page_size: r.pagination.page_size ?? 20,
        });
    });
  }, [fetchTable, demographicsPage]);
  useEffect(() => {
    fetchTable("positioning", positioningPage, 20, setPositioningLoading).then(
      (r) => {
        setPositioning(r.items as PositioningTableItem[]);
        if (r.pagination)
          setPositioningPag({
            current_page: r.pagination.current_page ?? 1,
            total_items: r.pagination.total_items ?? 0,
            total_pages: r.pagination.total_pages ?? 0,
            page_size: r.pagination.page_size ?? 20,
          });
      },
    );
  }, [fetchTable, positioningPage]);
  useEffect(() => {
    fetchTable(
      "url-destination",
      urlDestinationPage,
      20,
      setUrlDestinationLoading,
    ).then((r) => {
      setUrlDestination(r.items as UrlDestinationTableItem[]);
      setUrlDestinationTotal(r.totalRow as UrlDestinationTableItem | undefined);
      if (r.pagination)
        setUrlDestinationPag({
          current_page: r.pagination.current_page ?? 1,
          total_items: r.pagination.total_items ?? 0,
          total_pages: r.pagination.total_pages ?? 0,
          page_size: r.pagination.page_size ?? 20,
        });
    });
  }, [fetchTable, urlDestinationPage]);
  useEffect(() => {
    fetchTable(
      "creative-type",
      creativeTypePage,
      20,
      setCreativeTypeLoading,
    ).then((r) => {
      setCreativeType(r.items as CreativeTypeTableItem[]);
      setCreativeTypeTotal(r.totalRow as CreativeTypeTableItem | undefined);
      if (r.pagination)
        setCreativeTypePag({
          current_page: r.pagination.current_page ?? 1,
          total_items: r.pagination.total_items ?? 0,
          total_pages: r.pagination.total_pages ?? 0,
          page_size: r.pagination.page_size ?? 20,
        });
    });
  }, [fetchTable, creativeTypePage]);
  useEffect(() => {
    fetchTable("campaigns", campaignsPage, 20, setCampaignsLoading).then(
      (r) => {
        setCampaigns(r.items as CampaignTableItem[]);
        if (r.pagination)
          setCampaignsPag({
            current_page: r.pagination.current_page ?? 1,
            total_items: r.pagination.total_items ?? 0,
            total_pages: r.pagination.total_pages ?? 0,
            page_size: r.pagination.page_size ?? 20,
          });
      },
    );
  }, [fetchTable, campaignsPage]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchAds();
  }, [fetchAds]);

  // ─── Computed heatmap values ──────────────────────────────────────────────

  // Hourly
  const hourlyLpvValues = useMemo(
    () => hourlyData.map((h) => Number(h.pagDestino || 0)).filter((v) => v > 0),
    [hourlyData],
  );
  const hourlyInvValues = useMemo(
    () =>
      hourlyData.map((h) => Number(h.investimento || 0)).filter((v) => v > 0),
    [hourlyData],
  );

  // Demographics
  const demoInvValues = useMemo(
    () =>
      demographics.map((d) => Number(d.investimento || 0)).filter((v) => v > 0),
    [demographics],
  );
  const demoCompValues = useMemo(
    () => demographics.map((d) => Number(d.compras || 0)).filter((v) => v > 0),
    [demographics],
  );
  const demoRecValues = useMemo(
    () => demographics.map((d) => Number(d.receita || 0)).filter((v) => v > 0),
    [demographics],
  );
  const demoRoasValues = useMemo(
    () => demographics.map((d) => Number(d.roas || 0)).filter((v) => v > 0),
    [demographics],
  );

  // Positioning
  const posInvValues = useMemo(
    () =>
      positioning.map((p) => Number(p.investimento || 0)).filter((v) => v > 0),
    [positioning],
  );
  const posCompValues = useMemo(
    () => positioning.map((p) => Number(p.compras || 0)).filter((v) => v > 0),
    [positioning],
  );
  const posRecValues = useMemo(
    () => positioning.map((p) => Number(p.receita || 0)).filter((v) => v > 0),
    [positioning],
  );
  const posRoasValues = useMemo(
    () => positioning.map((p) => Number(p.roas || 0)).filter((v) => v > 0),
    [positioning],
  );

  // URL Destination
  const urlInvValues = useMemo(
    () =>
      urlDestination
        .map((u) => Number(u.investimento || 0))
        .filter((v) => v > 0),
    [urlDestination],
  );
  const urlCompValues = useMemo(
    () =>
      urlDestination.map((u) => Number(u.compras || 0)).filter((v) => v > 0),
    [urlDestination],
  );
  const urlRecValues = useMemo(
    () =>
      urlDestination.map((u) => Number(u.receita || 0)).filter((v) => v > 0),
    [urlDestination],
  );
  const urlRoasValues = useMemo(
    () => urlDestination.map((u) => Number(u.roas || 0)).filter((v) => v > 0),
    [urlDestination],
  );

  // Creative Type
  const ctInvValues = useMemo(
    () =>
      creativeType.map((c) => Number(c.investimento || 0)).filter((v) => v > 0),
    [creativeType],
  );
  const ctCompValues = useMemo(
    () => creativeType.map((c) => Number(c.compras || 0)).filter((v) => v > 0),
    [creativeType],
  );
  const ctRecValues = useMemo(
    () => creativeType.map((c) => Number(c.receita || 0)).filter((v) => v > 0),
    [creativeType],
  );
  const ctRoasValues = useMemo(
    () => creativeType.map((c) => Number(c.roas || 0)).filter((v) => v > 0),
    [creativeType],
  );

  // Campaigns
  const campInvValues = useMemo(
    () =>
      campaigns.map((c) => Number(c.investimento || 0)).filter((v) => v > 0),
    [campaigns],
  );
  const campCompValues = useMemo(
    () => campaigns.map((c) => Number(c.compras || 0)).filter((v) => v > 0),
    [campaigns],
  );
  const campRecValues = useMemo(
    () => campaigns.map((c) => Number(c.receita || 0)).filter((v) => v > 0),
    [campaigns],
  );
  const campRoasValues = useMemo(
    () => campaigns.map((c) => Number(c.roas || 0)).filter((v) => v > 0),
    [campaigns],
  );

  // Ads
  const adsInvValues = useMemo(
    () =>
      ads
        .map((a) => Number(a.investimento || a.spend || 0))
        .filter((v) => v > 0),
    [ads],
  );
  const adsCompValues = useMemo(
    () => ads.map((a) => Number(a.compras || 0)).filter((v) => v > 0),
    [ads],
  );
  const adsRecValues = useMemo(
    () => ads.map((a) => Number(a.receita || 0)).filter((v) => v > 0),
    [ads],
  );
  const adsRoasValues = useMemo(
    () => ads.map((a) => Number(a.roas || 0)).filter((v) => v > 0),
    [ads],
  );

  // ─── Column definitions ───────────────────────────────────────────────────

  // 1. Hourly table
  const colunasHourly: TableColumn[] = useMemo(
    () => [
      {
        key: "hora",
        header: "Hora",
        align: "left" as const,
        render: (v) => (
          <span className="text-xs font-medium">{v as string}</span>
        ),
      },
      {
        key: "pagDestino",
        header: "Visualização Pág. Destino",
        align: "right" as const,
        render: (v) =>
          renderBarCell(v, hourlyLpvValues, "#22c55e", (n) => formatNumber(n)),
      },
      {
        key: "investimento",
        header: "Investimento",
        align: "right" as const,
        render: (v) =>
          renderBarCell(v, hourlyInvValues, "#f59e0b", formatCurrency),
      },
      {
        key: "deltaInvestimento",
        header: "% Δ",
        align: "right" as const,
        render: renderDelta,
      },
      {
        key: "ticketMedio",
        header: "TM",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatCurrency(n)
          );
        },
      },
      {
        key: "txConversao",
        header: "TX Conversão",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${formatDecimal(n, 2)}%`
          );
        },
      },
      {
        key: "roas",
        header: "ROAS",
        align: "right" as const,
        render: (v) =>
          renderHeatmap(
            v,
            hourlyData.map((h) => Number(h.roas || 0)).filter((x) => x > 0),
            "34, 197, 94",
            formatRoas,
          ),
      },
      {
        key: "receita",
        header: "Receita",
        align: "right" as const,
        render: (v) =>
          renderHeatmap(
            v,
            hourlyData.map((h) => Number(h.receita || 0)).filter((x) => x > 0),
            "6, 182, 212",
            formatCurrency,
          ),
      },
      {
        key: "delta",
        header: "% Δ",
        align: "right" as const,
        render: renderDelta,
      },
    ],
    [hourlyLpvValues, hourlyInvValues, hourlyData],
  );

  // 2. Demographics columns (Gênero + Idade)
  const colunasDemographics: TableColumn[] = useMemo(
    () => [
      { key: "genero", header: "Gênero", align: "left" as const },
      { key: "idade", header: "Idade", align: "left" as const },
      {
        key: "pagDestino",
        header: "Pág. Destino",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatNumber(n)
          );
        },
      },
      {
        key: "custoPagDestino",
        header: "Custo Pág. Destino",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatCurrency(n)
          );
        },
      },
      {
        key: "txConversao",
        header: "TX Conversão",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${formatDecimal(n, 2)}%`
          );
        },
      },
      {
        key: "investimento",
        header: "Investimento",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, demoInvValues, "20, 184, 166", formatCurrency),
      },
      {
        key: "compras",
        header: "Compras",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, demoCompValues, "168, 85, 247", (n) =>
            formatNumber(n),
          ),
      },
      {
        key: "receita",
        header: "Receita",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, demoRecValues, "6, 182, 212", formatCurrency),
      },
      {
        key: "roas",
        header: "ROAS",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, demoRoasValues, "34, 197, 94", formatRoas),
      },
      {
        key: "delta",
        header: "% Δ",
        align: "right" as const,
        render: renderDelta,
      },
    ],
    [demoInvValues, demoCompValues, demoRecValues, demoRoasValues],
  );

  // 3. Positioning columns
  const colunasPositioning: TableColumn[] = useMemo(
    () =>
      buildBreakdownColumns(
        "posicionamento",
        "Posicionamento",
        posInvValues,
        posCompValues,
        posRecValues,
        posRoasValues,
      ),
    [posInvValues, posCompValues, posRecValues, posRoasValues],
  );

  // 4. URL Destination columns
  const colunasUrlDestination: TableColumn[] = useMemo(
    () =>
      buildBreakdownColumns(
        "urlDestino",
        "URL Destino",
        urlInvValues,
        urlCompValues,
        urlRecValues,
        urlRoasValues,
      ),
    [urlInvValues, urlCompValues, urlRecValues, urlRoasValues],
  );

  // 5. Creative Type columns
  const colunasCreativeType: TableColumn[] = useMemo(
    () =>
      buildBreakdownColumns(
        "creativeType",
        "Ad Creative Object Type",
        ctInvValues,
        ctCompValues,
        ctRecValues,
        ctRoasValues,
      ),
    [ctInvValues, ctCompValues, ctRecValues, ctRoasValues],
  );

  // 6. Campaign columns
  const colunasCampanha: TableColumn[] = useMemo(
    () =>
      buildBreakdownColumns(
        "campanha",
        "Campanha",
        campInvValues,
        campCompValues,
        campRecValues,
        campRoasValues,
      ),
    [campInvValues, campCompValues, campRecValues, campRoasValues],
  );

  // 7. Ad columns (with thumbnail + details)
  const colunasAds: TableColumn[] = useMemo(
    () => [
      {
        key: "ad_name",
        header: "Anúncio",
        align: "left" as const,
        render: (v, row) => {
          const ad = row as MetaAdsAdPerformanceItem;
          return (
            <div className="flex items-center gap-3 min-w-[200px]">
              {ad.thumbnail_url ? (
                <img
                  src={ad.thumbnail_url}
                  alt=""
                  className="w-10 h-10 object-cover rounded flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-10 h-10 bg-muted rounded flex-shrink-0" />
              )}
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-medium truncate">
                  {ad.ad_name || "—"}
                </span>
                <span className="text-[10px] text-muted-foreground truncate">
                  {ad.campaign_name || ""}
                  {ad.campaign_name && ad.adset_name ? " › " : ""}
                  {ad.adset_name || ""}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: "impressions",
        header: "Impressões",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatNumber(n)
          );
        },
      },
      {
        key: "reach",
        header: "Alcance",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatNumber(n)
          );
        },
      },
      {
        key: "link_ctr",
        header: "CTR",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${formatDecimal(n, 2)}%`
          );
        },
      },
      {
        key: "cpc",
        header: "CPC",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            formatCurrency(n)
          );
        },
      },
      {
        key: "investimento",
        header: "Investimento",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, adsInvValues, "20, 184, 166", formatCurrency),
      },
      {
        key: "compras",
        header: "Compras",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, adsCompValues, "168, 85, 247", (n) =>
            formatNumber(n),
          ),
      },
      {
        key: "receita",
        header: "Receita",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, adsRecValues, "6, 182, 212", formatCurrency),
      },
      {
        key: "roas",
        header: "ROAS",
        align: "right" as const,
        sortable: true,
        render: (v) =>
          renderHeatmap(v, adsRoasValues, "34, 197, 94", formatRoas),
      },
      {
        key: "txConversao",
        header: "TX Conversão",
        align: "right" as const,
        render: (v) => {
          const n = Number(v || 0);
          return n === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            `${formatDecimal(n, 2)}%`
          );
        },
      },
    ],
    [adsInvValues, adsCompValues, adsRecValues, adsRoasValues],
  );

  // ─── Filtered ads ─────────────────────────────────────────────────────────
  const filteredAds = useMemo(() => {
    if (!adSearch.trim()) return ads;
    const q = adSearch.trim().toLowerCase();
    return ads.filter((a) => {
      const name = (a.ad_name || "").toLowerCase();
      switch (adFilterMode) {
        case "equals":
          return name === q;
        case "contains":
          return name.includes(q);
        case "startsWith":
          return name.startsWith(q);
        case "regex":
          try {
            return new RegExp(adSearch.trim(), "i").test(a.ad_name || "");
          } catch {
            return false;
          }
        case "in":
          return q
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .some((term) => name.includes(term));
        default:
          return name.includes(q);
      }
    });
  }, [ads, adSearch, adFilterMode]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 page-background">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard/ads")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <div>
          <h1 className="text-lg font-bold">MELHORES RESULTADOS</h1>
          <p className="text-sm text-muted-foreground">
            Análise detalhada da performance Meta Ads
          </p>
        </div>
      </div>

      <GlobalFilter />

      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {KPI_METRICS_LIST.map(({ slug, label, fmt }) => {
          const kpi = kpis[slug];
          return (
            <Card key={slug} className="card-kpi-primary">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-sm font-medium">
                  <span className="inline-flex items-center gap-1">
                    <span className="truncate">{label}</span>
                    <HelpTooltip
                      content={getKpiTooltip(label)}
                      triggerClassName="h-3.5 w-3.5 text-[9px]"
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 pt-0">
                {kpisLoading ? (
                  <CarregandoValor className="text-2xl" />
                ) : kpi ? (
                  <div className="flex flex-col">
                    <EstadoDoValor
                      carregando={false}
                      disponivel={Number.isFinite(kpi.value)}
                      fontes={fontesDoKpi(slug)}
                      className="text-sm text-muted-foreground"
                    >
                      <KpiNumber
                        value={kpi.value}
                        format={(n) => fmt(n)}
                        className="font-light text-foreground"
                        maxFontPx={28}
                        breathingRoomPx={16}
                        ariaLabel={label}
                      />
                    </EstadoDoValor>
                    {comparisonEnabled && typeof kpi.change === "number" && (
                      <span
                        className={`inline-flex items-center gap-0.5 text-xs ${kpi.trend === "up" ? "text-success-soft" : kpi.trend === "down" ? "text-error-soft" : "text-muted-foreground"}`}
                      >
                        {kpi.trend === "up" ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : kpi.trend === "down" ? (
                          <TrendingDown className="w-3 h-3" />
                        ) : null}
                        {formatSignedPercent(kpi.change, 1)}
                      </span>
                    )}
                  </div>
                ) : (
                  <ValorIndisponivel
                    fontes={fontesDoKpi(slug)}
                    erro={erroKpis}
                    className="text-sm text-muted-foreground"
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 1. PERFORMANCE POR HORA */}
      <StandardizedTable
        title="PERFORMANCE POR HORA"
        columns={colunasHourly}
        data={hourlyData}
        hideTotals
        loading={hourlyLoading}
      />

      {/* 2. GÊNERO + IDADE */}
      <StandardizedTable
        title="GÊNERO + IDADE"
        columns={colunasDemographics}
        data={demographics}
        loading={demographicsLoading}
      />
      <Paginator
        current={demographicsPag.current_page}
        total={demographicsPag.total_pages}
        totalItems={demographicsPag.total_items}
        pageSize={demographicsPag.page_size}
        onPage={setDemographicsPage}
      />

      {/* 3. POSICIONAMENTO */}
      <StandardizedTable
        title="POSICIONAMENTO"
        columns={colunasPositioning}
        data={positioning}
        loading={positioningLoading}
      />
      <Paginator
        current={positioningPag.current_page}
        total={positioningPag.total_pages}
        totalItems={positioningPag.total_items}
        pageSize={positioningPag.page_size}
        onPage={setPositioningPage}
      />

      {/* 4. URL DESTINO */}
      <StandardizedTable
        title="URL DESTINO"
        columns={colunasUrlDestination}
        data={urlDestination}
        total={totalDoServidor(urlDestinationTotal)}
        loading={urlDestinationLoading}
      />
      <Paginator
        current={urlDestinationPag.current_page}
        total={urlDestinationPag.total_pages}
        totalItems={urlDestinationPag.total_items}
        pageSize={urlDestinationPag.page_size}
        onPage={setUrlDestinationPage}
      />

      {/* 5. AD CREATIVE OBJECT TYPE */}
      <StandardizedTable
        title="AD CREATIVE OBJECT TYPE"
        columns={colunasCreativeType}
        data={creativeType}
        total={totalDoServidor(creativeTypeTotal)}
        loading={creativeTypeLoading}
      />
      <Paginator
        current={creativeTypePag.current_page}
        total={creativeTypePag.total_pages}
        totalItems={creativeTypePag.total_items}
        pageSize={creativeTypePag.page_size}
        onPage={setCreativeTypePage}
      />

      {/* 6. CAMPANHAS */}
      <StandardizedTable
        title="CAMPANHAS"
        columns={colunasCampanha}
        data={campaigns}
        loading={campaignsLoading}
      />
      <Paginator
        current={campaignsPag.current_page}
        total={campaignsPag.total_pages}
        totalItems={campaignsPag.total_items}
        pageSize={campaignsPag.page_size}
        onPage={setCampaignsPage}
      />

      {/* 7. NOME DO ANÚNCIO */}
      <div className="space-y-2">
        <Card className="card-kpi-secondary">
          <CardHeader className="pb-2">
            <CardTitle>NOME DO ANÚNCIO</CardTitle>
          </CardHeader>
          <CardContent className="pb-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative" ref={adFilterRef}>
                <button
                  type="button"
                  onClick={() => setAdFilterOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border rounded px-2 py-1.5 bg-background transition-colors"
                >
                  {
                    AD_FILTER_OPTIONS.find((o) => o.value === adFilterMode)
                      ?.label
                  }
                  <ChevronDown className="w-3 h-3" />
                </button>
                {adFilterOpen && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[140px] bg-popover border border-border rounded-md shadow-lg py-1">
                    {AD_FILTER_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setAdFilterMode(opt.value);
                          setAdFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors ${
                          adFilterMode === opt.value
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-popover-foreground"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Input
                placeholder="Insira um valor"
                value={adSearch}
                onChange={(e) => setAdSearch(e.target.value)}
                className="max-w-xs h-8 text-xs"
              />
            </div>
          </CardContent>
        </Card>
        <StandardizedTable
          title="Anúncios"
          columns={colunasAds}
          data={filteredAds}
          loading={adsLoading}
        />
        <Paginator
          current={adsPag.current_page}
          total={adsPag.total_pages}
          totalItems={adsPag.total_results}
          pageSize={20}
          onPage={setAdsPage}
        />
      </div>
    </div>
  );
};

export default MelhoresResultadosMetaPage;
